/**
 * The projects this window's World holds (RFC 0281 S2).
 *
 * The World pointer already exists and is thrown away: `ProjectRedirect` reads
 * it once, takes the FIRST project registered, redirects to `/:worldId/:projectId`,
 * and never looks again. That single read is what makes a window a one project
 * window. This is the same pointer, kept.
 *
 * Follows `useWorldSkills` and `useWorldDevices` exactly, because it is the same
 * shape of question asked of the same document: read the pointer, project the
 * registry, memo the result. The projection lives in mod-core so the cli and iOS
 * answer it identically.
 *
 * NOT A PICKER. A project is not a mode (RFC 0281 §3), so nothing here selects
 * one — the list is what a sidebar renders as collapsible roots, and which
 * project a pane is on is a fact about that pane.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { projectWorldProjects, type WorldProjectRef } from '@mod/core/model'
import { useMemo } from 'react'
import { useDocument } from './repo/useDocument'
import { readWorldPointer } from './world'

/**
 * Every project in this World, sorted by display name.
 *
 * `[]` while the World is unread or unsynced rather than a throw, so a caller
 * renders an empty list instead of failing — the same contract
 * `projectWorldProjects` already keeps for a World that does not exist yet.
 */
export function useWorldProjects(): WorldProjectRef[] {
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  return useMemo(() => projectWorldProjects(worldDoc), [worldDoc])
}
